import type {
  IMfaEnrollment,
  IMfaMethodsState,
  MfaEnrollMethod,
  MfaOtpChannel,
  MfaVerifyMethod,
} from '@interfaces/IMfa';
import { isMfaOtpChannel, MFA_ENROLL_METHODS } from '@interfaces/IMfa';
import type { UnifiedProject } from '@interfaces/IUnifiedProject';
import { adoptSessionUser, credFetch, optionalProjects, type AuthUser } from './auth';

/**
 * The MFA leg of the login flow. Every call here is cookie-only: the browser
 * holds either `dorca_pending` (mid-login, ~10 min) or `dorca_sid` (full
 * session), and the BFF picks the right one server-side. Nothing in this module
 * reads or writes a token, and the engine's own cookies never reach the browser.
 *
 * Contracts: "D-ORCA MFA proxy contracts" in
 * docs/plans/2026-07-30-better-auth-mfa-implementation-plan.md.
 *
 * `credFetch` (not `authedFetch`) throughout: a 401 on these routes is an
 * expected, caller-interpreted outcome (wrong code, wrong password, dead
 * pending state) rather than a mid-session expiry, so it must not fire
 * `dorca:auth-expired` and yank the user out from under the challenge.
 */

/**
 * Neutral verification failure. The contract answers a bare 401 for a wrong
 * code, a replayed backup code, an exhausted OTP, AND an expired pending
 * state — and the UI must keep it that way. Never render anything more
 * specific than "the code didn't work; try again".
 */
export class MfaInvalidCodeError extends Error {
  constructor() {
    super('mfa verification failed');
    this.name = 'MfaInvalidCodeError';
  }
}

/**
 * The password re-prompt guarding enroll / disable was rejected. Distinct from
 * `MfaInvalidCodeError` because the user typed a different secret and needs to
 * be told which field to fix.
 */
export class MfaPasswordRejectedError extends Error {
  constructor() {
    super('mfa password check failed');
    this.name = 'MfaPasswordRejectedError';
  }
}

/**
 * The phone number was not in the international format the backend will store. The ONLY
 * form-shaped error in this module: everything else here is deliberately neutral, but a
 * mistyped number is the user's own input and telling them so is what lets them fix it.
 */
export class MfaInvalidPhoneError extends Error {
  constructor() {
    super('mfa phone number invalid');
    this.name = 'MfaInvalidPhoneError';
  }
}

/**
 * The only failure the backend deliberately makes distinguishable: the per-IP /
 * per-challenge brake, or an upstream account lockout. It says nothing about
 * whether the code was right, so surfacing it is safe — and silently showing
 * "wrong code" while every attempt is being refused would be actively
 * misleading.
 */
export class MfaRateLimitedError extends Error {
  constructor() {
    super('mfa attempts rate limited');
    this.name = 'MfaRateLimitedError';
  }
}

async function failGeneric(res: Response): Promise<never> {
  if (res.status === 429) throw new MfaRateLimitedError();
  const body: unknown = await res.json().catch(() => null);
  const message =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error?: { message?: unknown } }).error?.message ?? 'request failed')
      : 'request failed';
  throw new Error(`${res.status} ${res.statusText}: ${message}`);
}

/**
 * A promoted session: the same payload `/api/auth/login` returns on a
 * password-only success, so the post-login bookkeeping (including the
 * piggybacked project list that saves a `/api/projects` round-trip on first
 * paint) is identical whichever leg produced it.
 */
export interface IMfaSession {
  user: AuthUser;
  projects?: UnifiedProject[];
}

/**
 * POST /api/auth/mfa/verify {method, code} → `200 {user}` (+ `dorca_sid`).
 * A 401 is the neutral failure; see `MfaInvalidCodeError`.
 */
export async function verifyMfa(method: MfaVerifyMethod, code: string): Promise<IMfaSession> {
  const res = await credFetch('/api/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ method, code }),
  });
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  return { user: adoptSessionUser(data, 'verifyMfa'), projects: optionalProjects(data) };
}

/**
 * POST /api/auth/mfa/send-otp {} → 204. Only valid while the pending state
 * lists `otp` among its allowed methods; anything else is a protocol error the
 * caller surfaces as "couldn't send".
 */
export async function sendMfaOtp(channel?: MfaOtpChannel): Promise<void> {
  const res = await credFetch('/api/auth/mfa/send-otp', {
    method: 'POST',
    // `channel` means "send it here instead of my default". Omitted ⇒ the principal channel,
    // which is what every caller wants unless the user explicitly picked another.
    body: JSON.stringify(channel === undefined ? {} : { channel }),
  });
  if (!res.ok) await failGeneric(res);
}

/** A string[] of backup codes, or `undefined` when the field is absent. */
function optionalBackupCodes(v: unknown): string[] | undefined {
  return Array.isArray(v) && v.every((c) => typeof c === 'string') ? (v as string[]) : undefined;
}

function isEnrollment(v: unknown): v is IMfaEnrollment {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.totpUri !== 'string') return false;
  // `backupCodes` is optional now: present on a first-factor enrolment, absent
  // when an authenticator is added to an account that already has a factor
  // (the backend reuses the existing secret). Reject only a *malformed* value.
  return r.backupCodes === undefined || optionalBackupCodes(r.backupCodes) !== undefined;
}

/**
 * Narrow a `MfaEnrollMethod[]` out of an untrusted array, PRESERVING the server's order — the
 * engine documents `allowed` as "in UI preference order", so re-emitting our own hard-coded
 * order would silently ignore an environment that prefers email first.
 */
function parseEnrollMethods(v: unknown): MfaEnrollMethod[] {
  if (!Array.isArray(v)) return [];
  const known = new Set<string>(MFA_ENROLL_METHODS);
  return v.filter((m): m is MfaEnrollMethod => typeof m === 'string' && known.has(m));
}

/**
 * POST /api/auth/mfa/enroll {password} → `200 {totpUri, backupCodes?}`. Valid
 * from `dorca_pending` (forced enrollment) or a full session (voluntary, from
 * the security settings). A 401 means the password was rejected — or, in the
 * forced case, that the pending state died; the views arm their own TTL timer
 * for the latter because the contract can't tell them apart.
 */
export async function enrollMfa(password: string): Promise<IMfaEnrollment> {
  const res = await credFetch('/api/auth/mfa/enroll', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) throw new MfaPasswordRejectedError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  if (!isEnrollment(data)) throw new Error('enrollMfa: malformed enrollment response');
  return data;
}

/**
 * `POST /api/auth/mfa/enroll/confirm` answers differently depending on which
 * credential authorised the enrollment:
 *  - from `dorca_pending` (forced, mid-login) → `{user}`, session promoted;
 *  - from a full session (voluntary, from settings) → `{enabled: true}`.
 */
export type MfaEnrollConfirmResult =
  | { kind: 'authenticated'; session: IMfaSession }
  | { kind: 'enabled' };

/**
 * POST /api/auth/mfa/enroll/confirm {code}. A 401 is the same neutral
 * code-failure as `verifyMfa`.
 */
export async function confirmMfaEnrollment(code: string): Promise<MfaEnrollConfirmResult> {
  const res = await credFetch('/api/auth/mfa/enroll/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  // The voluntary path answers `{enabled: true}`; the forced path answers a
  // user profile. Check the cheap discriminator first so a body carrying both
  // (a future superset) still promotes the session.
  if (typeof data === 'object' && data !== null && 'user' in data) {
    return {
      kind: 'authenticated',
      session: {
        user: adoptSessionUser(data, 'confirmMfaEnrollment'),
        projects: optionalProjects(data),
      },
    };
  }
  return { kind: 'enabled' };
}

/**
 * GET /api/auth/mfa/methods → `200 {enrolled, allowed}`. Full session only; a
 * 401 is the neutral failure (no live session), never a password rejection.
 * `enrolled` empty ⇒ 2FA is off.
 */
export async function getMfaMethods(): Promise<IMfaMethodsState> {
  const res = await credFetch('/api/auth/mfa/methods', { method: 'GET' });
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  const r = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;
  const enrolled = parseEnrollMethods(r.enrolled);
  return {
    enrolled,
    allowed: parseEnrollMethods(r.allowed),
    twoFactorEnabled: typeof r.twoFactorEnabled === 'boolean' ? r.twoFactorEnabled : enrolled.length > 0,
    ...(isMfaOtpChannel(r.primaryOtpChannel) ? { primaryOtpChannel: r.primaryOtpChannel } : {}),
    ...(typeof r.maskedPhone === 'string' ? { maskedPhone: r.maskedPhone } : {}),
  };
}

/**
 * POST /api/auth/mfa/email/send {} → 204. Mails a 6-digit ownership code to the
 * account's email so the address can be proven before it is enrolled as a
 * factor. Full session only; a 401 is the neutral no-live-session failure (see
 * `MfaInvalidCodeError`) — never a password rejection, since no password is
 * involved in a send.
 */
export async function sendMfaEmailOtp(): Promise<void> {
  const res = await credFetch('/api/auth/mfa/email/send', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
}

/**
 * POST /api/auth/mfa/email/verify {code, password?} → `200 {backupCodes?}`.
 * Confirms the emailed ownership code and enrols email as a factor (turning 2FA
 * on). `password` is required only when email is the *first* factor (2FA
 * currently off) — the backend returns `backupCodes` then; when 2FA is already
 * on, send no password and no codes come back. A wrong/expired/exhausted code
 * OR a wrong/missing-when-required password all answer the same neutral 401,
 * surfaced as `MfaPasswordRejectedError` — the same password gate the caller
 * already handles. Full session only.
 */
export async function verifyMfaEmail(code: string, password?: string): Promise<{ backupCodes?: string[] }> {
  const res = await credFetch('/api/auth/mfa/email/verify', {
    method: 'POST',
    body: JSON.stringify({ code, ...(password !== undefined ? { password } : {}) }),
  });
  if (res.status === 401) throw new MfaPasswordRejectedError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json().catch(() => null);
  const backupCodes = optionalBackupCodes((data as Record<string, unknown> | null)?.backupCodes);
  return backupCodes ? { backupCodes } : {};
}

/**
 * POST /api/auth/mfa/sms/send {phone} → 204. Texts a 6-digit ownership code to the number the
 * user is claiming, so it can be proven before it guards their sign-in. The number must be in
 * international format (`+8190…`): a local one cannot be resolved without a country, which the
 * account does not record, so the form offers a country selector rather than guessing. A 400 is
 * that format refusal — the one MFA failure here that is a FORM error and should be shown as one.
 * Full session only.
 */
export async function sendMfaSmsOtp(phone: string): Promise<void> {
  const res = await credFetch('/api/auth/mfa/sms/send', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
  if (res.status === 400) throw new MfaInvalidPhoneError();
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
}

/**
 * POST /api/auth/mfa/sms/verify {code, password?} → `200 {backupCodes?}`. Same contract and same
 * first-factor password rule as `verifyMfaEmail`; on success the PROVEN number is stored and
 * `sms` is enrolled.
 */
export async function verifyMfaSms(code: string, password?: string): Promise<{ backupCodes?: string[] }> {
  const res = await credFetch('/api/auth/mfa/sms/verify', {
    method: 'POST',
    body: JSON.stringify({ code, ...(password !== undefined ? { password } : {}) }),
  });
  if (res.status === 401) throw new MfaPasswordRejectedError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json().catch(() => null);
  const backupCodes = optionalBackupCodes((data as Record<string, unknown> | null)?.backupCodes);
  return backupCodes ? { backupCodes } : {};
}

/**
 * POST /api/auth/mfa/primary-channel {channel} → 204. Chooses which enrolled channel a future
 * sign-in challenge is delivered on by default. Full session only; the backend refuses a channel
 * the user has not enrolled (as the neutral 401, like every other MFA failure).
 */
export async function setPrimaryOtpChannel(channel: MfaOtpChannel): Promise<void> {
  const res = await credFetch('/api/auth/mfa/primary-channel', {
    method: 'POST',
    body: JSON.stringify({ channel }),
  });
  if (res.status === 401) throw new MfaInvalidCodeError();
  if (!res.ok) await failGeneric(res);
}

/**
 * POST /api/auth/mfa/backup-codes {password} → `200 {backupCodes}`. Mints a FRESH recovery set
 * and discards the previous one upstream — the old codes stop working the moment this returns, so
 * the caller must show the new set once and say so. Password-gated: a 401 is the password
 * rejection, same as `enrollMfa`. Full session only.
 */
export async function regenerateBackupCodes(password: string): Promise<string[]> {
  const res = await credFetch('/api/auth/mfa/backup-codes', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  if (res.status === 401) throw new MfaPasswordRejectedError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  const codes = optionalBackupCodes((data as Record<string, unknown> | null)?.backupCodes);
  if (!codes || codes.length === 0) {
    throw new Error('regenerateBackupCodes: malformed response');
  }
  return codes;
}

/**
 * POST /api/auth/mfa/methods/unenroll {method, password} → `200 {enrolled}`. Removes one
 * factor and answers with the surviving set; removing the last turns 2FA off
 * (an empty `enrolled`). A 401 is neutral. Full session only.
 */
export async function unenrollMfaMethod(
  method: MfaEnrollMethod,
  password: string,
): Promise<{ enrolled: MfaEnrollMethod[] }> {
  const res = await credFetch('/api/auth/mfa/methods/unenroll', {
    method: 'POST',
    body: JSON.stringify({ method, password }),
  });
  // Password gated: removing the last factor turns 2FA off, so this asks for the same proof
  // /mfa/disable did. A 401 is the password rejection.
  if (res.status === 401) throw new MfaPasswordRejectedError();
  if (!res.ok) await failGeneric(res);
  const data: unknown = await res.json();
  const r = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;
  return { enrolled: parseEnrollMethods(r.enrolled) };
}

/**
 * Which factors the challenge screen offers, given the `methods` the login
 * response allowed.
 *
 * `backup` has to be derived rather than read: the contract's `methods` array
 * only ever carries `totp` / `otp`, yet `verify` accepts `backup`. Per the
 * proxy's own schema, backup is "the recovery path, available whenever a
 * challenge is outstanding" — so it is appended to any non-empty challenge, and
 * only omitted when there is no challenge at all.
 */
export function challengeMethodsToOffer(methods: readonly MfaVerifyMethod[]): MfaVerifyMethod[] {
  const offered: MfaVerifyMethod[] = methods.filter((m) => m === 'totp' || m === 'otp');
  if (offered.length > 0) offered.push('backup');
  return offered;
}
